/**
 * In-memory handoff for direct-to-S3 upload across route navigation.
 * If the user refreshes mid-upload, state is lost (expected).
 */

const UPLOAD_DONE_KEY = 'beachvision_upload_done_session';

/** @type {null | { sessionId: string|number, file: File, spec: object, phase: string, progress: number, error: string|null }} */
let pending = null;

/**
 * Example-video handoff: session is created, then SessionDetail runs upload/example/.
 * @type {null | { sessionId: string|number, exampleVideoName: string, phase: 'pending'|'running'|'error', error: string|null }}
 */
let exampleVideoPending = null;

const listeners = new Set();

function emit() {
  listeners.forEach((cb) => cb());
}

/**
 * @param {{ sessionId: string|number, file: File, spec: object }} payload
 */
export function setPendingDirectUpload(payload) {
  pending = {
    sessionId: payload.sessionId,
    file: payload.file,
    spec: payload.spec,
    phase: 'pending',
    progress: 0,
    error: null,
  };
  emit();
}

export function getPendingDirectUpload() {
  return pending;
}

/**
 * @param {string|number} sessionId
 */
export function getPendingForSession(sessionId) {
  if (!pending || String(pending.sessionId) !== String(sessionId)) return null;
  return pending;
}

/**
 * @param {Partial<{ phase: string, progress: number, error: string|null }>} patch
 */
export function patchPendingDirectUpload(patch) {
  if (!pending) return;
  Object.assign(pending, patch);
  emit();
}

export function clearPendingDirectUpload() {
  pending = null;
  emit();
}

export function markSessionUploadCompletedInStorage(sessionId) {
  try {
    sessionStorage.setItem(`${UPLOAD_DONE_KEY}_${sessionId}`, '1');
  } catch {
    /* ignore quota / private mode */
  }
}

export function isSessionUploadMarkedDone(sessionId) {
  try {
    return sessionStorage.getItem(`${UPLOAD_DONE_KEY}_${sessionId}`) === '1';
  } catch {
    return false;
  }
}

export function clearSessionUploadMarker(sessionId) {
  try {
    sessionStorage.removeItem(`${UPLOAD_DONE_KEY}_${sessionId}`);
  } catch {
    /* ignore */
  }
}

export function subscribeDirectUpload(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * @param {{ sessionId: string|number, exampleVideoName: string }} payload
 */
export function setExampleVideoPending(payload) {
  exampleVideoPending = {
    sessionId: payload.sessionId,
    exampleVideoName: payload.exampleVideoName,
    phase: 'pending',
    error: null,
  };
  emit();
}

export function getExampleVideoPending() {
  return exampleVideoPending;
}

/**
 * @param {string|number} sessionId
 */
export function getExampleVideoPendingForSession(sessionId) {
  if (!exampleVideoPending || String(exampleVideoPending.sessionId) !== String(sessionId)) {
    return null;
  }
  return exampleVideoPending;
}

/**
 * @param {Partial<{ phase: string, error: string|null }>} patch
 */
export function patchExampleVideoPending(patch) {
  if (!exampleVideoPending) return;
  Object.assign(exampleVideoPending, patch);
  emit();
}

export function clearExampleVideoPending() {
  exampleVideoPending = null;
  emit();
}
